/** @format */

import { Button } from "@ui-library/core"
import { ChevronLeftRegular } from "@ui-library/icons"
import bffClient from "_clients/BFFClient"
import { GetMembersDistributionDistributionControllerResponse } from "_clients/BFFClient.generated"
import { NoSearchResultMember } from "_components/atoms/NoSearchResultMember/NoSearchResultMember"
import CreditDispatchTable from "_components/molecules/CreditDispatchTable/CreditDispatchTable"
import { DistributionChangeType } from "_components/molecules/CreditDispatchTable/CreditDispatchTable.types"
import DashboardDistributionHeader from "_components/molecules/DashboardDistributionHeader/DashboardDistributionHeader"
import DashBoardFilterBar from "_components/molecules/DashBoardFilterBar/DashBoardFilterBar"
import { isAbortError } from "_shared/helpers/errorHelper"
import {
	ChangeUrlQueryValues,
	GetQueryValues,
} from "_shared/helpers/routerHelper"
import { useAvailableBoostStore } from "_stores/availableBoost/availableBoost"
import useMarketStore from "_stores/market"
import _, { debounce } from "lodash"
import querystring from "querystring"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useRelativeRouter } from '../../../shared/hooks/useRelativeRouter'
import Dashboard from "../Dashboard/Dashboard"
import { getProductTypeEnumFromId } from "../Dashboard/Dashboard.types"
import {
	Container,
	DistributionContainer,
} from "./CreditDispatchDashboard.styled"
import {
	CreditDispatchDashboardProps,
	SearchParamsType,
} from "./CreditDispatchDashboard.types"
import { FakeData } from "./CreditDispatchDashbord.data"

export const getParmsForQuery = (path: string): SearchParamsType => {
	const routerParam = GetQueryValues(path)
	let params: SearchParamsType = {
		marketId: Number(routerParam.marketId),
		productType: getProductTypeEnumFromId(routerParam.productId),
		pageNumber: Number(routerParam.pageNumber ?? "1"),
		searchTerm: routerParam.searchTerm,
	}
	params = routerParam.networkId
		? { ...params, networkId: routerParam.networkId }
		: { ...params, contractId: routerParam.contractId }
	return params
}

const CreditDispatchDashboard = (props: CreditDispatchDashboardProps) => {
	const abortControllerRef = useRef<AbortController>(null)

	const router = useRelativeRouter();
	const { networkMarkets } = useMarketStore()

	const [pageLoading, setPageLoading] = useState<boolean>(true)

	const [data, setData] =
		useState<GetMembersDistributionDistributionControllerResponse>(FakeData)

	const [searchParams, setSearchParams] = useState<SearchParamsType>(
		getParmsForQuery(router.asPath)
	)

	const [searchText, setSearchText] = useState(
		getParmsForQuery(router.asPath).searchTerm
	)
	const timerRef = useRef<NodeJS.Timeout | null>(null)
	const [networkMarketDetails, setNetworkMarketDetails] = React.useState(null)
	const isNetworkMarketsLoading = networkMarkets.markets.length === 0

	const setSearchTerm = searchTerm => {
		if (timerRef.current) {
			clearTimeout(timerRef.current)
		}
		setSearchText(searchTerm)

		timerRef.current = setTimeout(() => {
			setPageLoading(true)
			setData(FakeData)
			const url = ChangeUrlQueryValues(router.asPath, {
				pageNumber: undefined,
				searchTerm: searchTerm != "" ? searchTerm : undefined,
			})
			router.push(url, undefined, { scroll: false })
		}, 1000)
	}

	const setPageNumber = useCallback(
		pageNumber => {
			const url = ChangeUrlQueryValues(router.asPath, { pageNumber })
			history.pushState({}, null, url)
			setPageLoading(true)
			setSearchParams(value => ({ ...value, pageNumber }))
		},
		[setPageLoading, setSearchParams, router.asPath]
	)

	useEffect(() => {
		const searchParams = getParmsForQuery(router.asPath)

		if (!searchParams.networkId && !searchParams.contractId) {
			router.push('/')
		}

		else {
			setSearchParams(searchParams)
			setSearchText(searchParams.searchTerm)
		}
	}, [router.asPath])

	useEffect(() => {
		const loadDistribution = debounce(
			async (searchParams: SearchParamsType) => {
				setPageLoading(true)
				try {
					if (abortControllerRef.current) {
						abortControllerRef.current.abort()
					}

					const newAbortController = new AbortController()
					abortControllerRef.current = newAbortController
					const response = props.networkId
						? await bffClient.getNetworkMemberDistribution(
								searchParams.networkId,
								searchParams.productType,
								searchParams.marketId,
								searchParams.searchTerm,
								undefined,
								searchParams.pageNumber,
								null,
								[],
								newAbortController.signal
						  )
						: await bffClient.getMultiBeneficiariesMemberDistribution(
								Number(props.contractId),
								searchParams.productType,
								searchParams.marketId,
								searchParams.searchTerm,
								undefined,
								searchParams.pageNumber,
								null,
								[],
								newAbortController.signal
						  )
					setData(response.result)
					setPageLoading(false)
				} catch (error) {					
					if (isAbortError(error)) {
						console.warn("Request aborted: Loading distribution", error);
						return;						
					}
					setPageLoading(false);					
					console.error("Error loading distribution", error)					
				}
			},
			searchParams.searchTerm ? 1000 : 0
		)
		loadDistribution(searchParams)
		return () => {
			loadDistribution.cancel()
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
		}
	}, [searchParams])

	const { findAvailableBoost } = useAvailableBoostStore()
	const [distributionChangeInProgress, setDistributionChangeInProgress] = useState<DistributionChangeType>(null);

	useEffect(() => {
		if (networkMarkets.markets?.length > 0) {
			const market = networkMarkets.markets.find(
				m => m.id === searchParams.marketId
			)
			const product = market?.products.find(
				p =>
					getProductTypeEnumFromId(p.id.toString()) === searchParams.productType
			)
			const contract = product?.contracts?.find(
				c => c.contractId == props.contractId
			)

			setNetworkMarketDetails({
				available: props.networkId
					? product.totalAvailableBoosts
					: contract?.available,
				assignedNotUsed: props.networkId
					? product.totalAssignedNotUsedBoosts
					: contract?.assignedNotUsed,
			})
		}
	}, [networkMarkets, searchParams])

	const handleBackClick = useCallback(() => {
		let newQuery = { ...router.query }
		delete newQuery.searchTerm
		delete newQuery.pageNumber
		router.push(`home?${querystring.stringify(newQuery)}`)
	}, [router])

	const hasRetrievedEmptyResults =
		!pageLoading && _.isEqual(data.members[0], FakeData.members[0])

	const isEligibleAutoSelection = searchParams.networkId && networkMarkets?.markets
	? networkMarkets?.markets?.find(m => m.id == searchParams.marketId)?.products?.find(p => searchParams.productType == getProductTypeEnumFromId(p.id.toString())).isEligibleAutoSelection ?? false
	: false;

	return !hasRetrievedEmptyResults || searchParams.searchTerm ? (
		<Container>
			<Button
				kind="primary"
				size="large"
				variant="text"
				tag="a"
				iconPosition="left"
				icon={ChevronLeftRegular}
				testID="btn-back-test-id"
				onClick={handleBackClick}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
			>
				Retour
			</Button>
			<Dashboard
				networkId={props.networkId}
				contractId={props.contractId}
				isHomePage={false}
				title={"Répartissez vos boosts"}
			/>

			<DistributionContainer>
				<DashboardDistributionHeader
					isLoading={isNetworkMarketsLoading}
					marketLabel={
						networkMarkets.markets.find(
							m => Number(m.id) === searchParams.marketId
						)?.label
					}
					isEligibleAutoSelection={isEligibleAutoSelection}
				/>
				<DashBoardFilterBar
					isLoading={isNetworkMarketsLoading}
					availableCreditsCount={
						findAvailableBoost(
							searchParams.networkId,
							searchParams.contractId,
							searchParams.marketId,
							searchParams.productType
						)?.newTotalAvailableBoosts ?? networkMarketDetails?.available
					}
					searchTerm={searchText}
					handleSearch={setSearchTerm}
					distributionChangeType={distributionChangeInProgress}
				/>
			</DistributionContainer>

			{data?.members && data.members.length > 0 ? (
				<CreditDispatchTable
					data={data}
					pageNumber={searchParams.pageNumber}
					networkId={searchParams.networkId}
					marketId={searchParams.marketId}
					contractId={props.contractId}
					productType={searchParams.productType}
					onPageChange={setPageNumber}
					isLoading={pageLoading}
					setDistributionChangeInProgress={setDistributionChangeInProgress}
				/>
			) : (
				<NoSearchResultMember
					hasSearchCriteria={searchParams.searchTerm != ""}
				/>
			)}
		</Container>
	) : (
		<></>
	)
}

export default CreditDispatchDashboard
